// What each wash includes, and the one line under each price. The only copy of
// this on the /services page: the table at 960px and up and the cards below it
// are two renderings of this one file, so the two widths cannot disagree.
//
// WHICH WASHES EXIST IS NOT IN HERE ANY MORE, and that is the point of the
// 5 October 2026 change. This file used to open with a `WASHES` array of six
// slugs and six names, and that array decided what the page advertised. Nine
// services were retired in the database in the same week; the page kept
// advertising them and they rendered as rows with a blank where the price
// should be. The list now arrives from the server (the gwmc:catalogue meta tag,
// lib/page-prices.js, fed by CustomerCatalogue) and this file holds only the
// two things the database has no opinion about: the descriptor copy and the
// feature matrix.
//
// WHERE THE TICKS COME FROM. Row for row, this is the `matrix` that used to
// live in pages/services/services-plans/index.js, which was the catalogue's
// only written-down form. Nothing was added to it and nothing was taken away
// in the rebuild: the counts still come out 3, 4, 8, 5, 12 and 10, and the
// first three mirror the lists in pages/home/sections/whats-included exactly,
// so the home page and this page cannot claim different things.
//
// It is a mirror and not an import for the same reason the home page's copy
// is a mirror of this one: importing across the two would drag a slot's
// stylesheet into the other's chunk. The fix is to lift this array into a
// shared data module both pages read, which is a file outside this page's
// directory. Flagged in the build report, along with three rows that look
// wrong and that only Barry can settle.
//
// PRICES ARE NOT IN HERE. They come from the gwmc:catalogue meta tag at render
// time (lib/page-prices.js). A number typed into this file would go stale and
// nobody would notice.

// `descriptor` is the line under the price and it is load-bearing, not
// decoration. DESIGN.md §1: the washes are told apart "by words and price,
// not by colour", and two pairs of them have no price difference to be told
// apart by. These six lines are the words.
//
// THEY WERE SENTENCES AND NOW THEY ARE A LADDER, which is the single biggest
// legibility change on this page. Each of the six was a free-written sentence
// of its own shape, three and four lines deep in a narrow column, and the
// six together were the tallest and the raggedest thing in the table. Worse,
// no two of them answered the same question in the same order, so comparing
// two washes meant reading two paragraphs and holding both.
//
// All six now say the same two things in the same two places: the work done
// outside the car, then the work done inside it. That is also exactly the
// axis the table's two row groups are cut on, so the descriptor is a one line
// preview of the column underneath it. Comparing two washes is now comparing
// a handful of named jobs, not two paragraphs:
//
//   Exterior Wash & Wax   Hand wash, wheels, wax.    Nothing inside.
//   Mini Valet            Hand wash, wheels, no wax. Vacuum inside.
//
// which is the £69 pair settled in one glance, and
//
//   Full Valet            Hard wax and engine bay.   Shampooed inside.
//   Dog Owners Valet      Hand wash, no wax.         Shampooed, pet hair out.
//
// which is the £215 pair. The pair sentences above the table carry what is
// left, because a descriptor that lists every difference is a paragraph
// again.
//
// THE WORDS ARE THE TRADE'S OWN AND EVERY ONE OF THEM IS A ROW BELOW. "Hand
// wash" is `Exterior hand wash`, "wheels" is `Wheels cleaned, tyres dressed`,
// "wax" is `Basic body wax`, "hard wax" is `HD Carnauba wax coating or
// protective paint sealant`, "engine bay" is `Engine bay cleaned`, "vacuum"
// is `Seats, carpet, mats and boot vacuumed`, "glass" is `Interior windows
// cleaned`, "shampooed" is `Upholstery shampooed, or leather cleaned and
// conditioned`, "pet hair out" is `Pet hair removal`. Nothing is claimed here
// that the matrix does not score, and nothing is softened into a benefit:
// "hard wax and engine bay" is a sentence somebody who does this for a living
// would write and "a thorough exterior treatment" is not.
//
// Every half is checked against the `in` matrix below. The Full Valet's line
// says "hard wax" and not "wax" because it scores the HD Carnauba row and not
// the basic body wax row; the Dog Owners Valet's says "no wax" because it
// scores neither.
//
// KEYED BY BOOKING SLUG, and a wash with no entry here keeps its database
// description instead (lib/service-catalogue.js). That is the safe default:
// the day a seventh wash is added to the catalogue it gets a column with the
// words the database carries, which is worse copy than these six and a great
// deal better than no column at all. Write it a line here when it earns one.
export const DESCRIPTORS = {
  exterior_wash_and_wax: 'Hand wash, wheels, wax. Nothing inside.',
  mini_valet: 'Hand wash, wheels, no wax. Vacuum inside.',
  mini_valet_plus: 'Hand wash, wheels, wax. Vacuum and glass.',
  interior_only_valet: 'Interior only. Shampooed throughout.',
  full_valet: 'Hard wax and engine bay. Shampooed inside.',
  dog_owners_valet: 'Hand wash, no wax. Shampooed, pet hair out.'
}

// The two same-price pairs, named by slug, for the two sentences above the
// table. A pair whose washes are not both on the page prints nothing: a
// sentence comparing a column that is not there is worse than no sentence.
export const PAIRS = [
  {
    washes: ['exterior_wash_and_wax', 'mini_valet'],
    note: 'One waxes the paint and never opens a door, the other vacuums and wipes inside and skips the wax.'
  },
  {
    washes: ['full_valet', 'dog_owners_valet'],
    note: 'One cleans the engine bay and hard waxes the paint, the other spends that time on pet hair.'
  }
]

// Every row every wash is scored against, in one order, so the eye can read
// straight down a column and count. Two groups, outside the car and inside
// it, because fourteen undifferentiated rows is a wall and because the group
// a wash scores nothing in is itself the answer: Exterior Wash & Wax is empty
// down the whole inside half, Interior Only Valet is empty down the whole
// outside half. On a phone, where there are no neighbouring columns to read
// that off, each card says it in words instead.
//
// `in` NAMES THE WASHES THAT SCORE THE ROW, by booking slug. It was a list of
// six booleans positionally indexed into the old `WASHES` array, which only
// worked while that array was a fixed six written in this file. The list is
// the server's now and it can be five washes or seven, so the matrix says
// which wash it means. A slug in here that the server does not publish scores
// nothing and costs nothing; a wash the server publishes that is in no row
// scores zero of fourteen, which is honest and visible rather than silent.
export const FEATURE_GROUPS = [
  {
    id: 'outside',
    title: 'Outside the car',
    // What a card says when a wash scores nothing in this group.
    none: 'Nothing outside the car. The paint, wheels and glass are left as they are.',
    rows: [
      {
        name: 'Exterior hand wash',
        in: ['exterior_wash_and_wax', 'mini_valet', 'mini_valet_plus', 'full_valet', 'dog_owners_valet']
      },
      {
        name: 'Wheels cleaned, tyres dressed',
        in: ['exterior_wash_and_wax', 'mini_valet', 'mini_valet_plus', 'full_valet', 'dog_owners_valet']
      },
      { name: 'Basic body wax', in: ['exterior_wash_and_wax', 'mini_valet_plus'] },
      { name: 'Boot and bonnet gutters cleaned', in: ['mini_valet_plus', 'full_valet', 'dog_owners_valet'] },
      {
        name: 'Screenwash refill, oil and fluid check',
        in: ['mini_valet_plus', 'full_valet', 'dog_owners_valet']
      },
      { name: 'Engine bay cleaned', in: ['full_valet'] },
      { name: 'HD Carnauba wax coating or protective paint sealant, your choice', in: ['full_valet'] }
    ]
  },
  {
    id: 'inside',
    title: 'Inside the car',
    none: 'Nothing inside the car. The seats, carpets and glass inside are left as they are.',
    rows: [
      {
        name: 'Seats, carpet, mats and boot vacuumed',
        in: ['mini_valet', 'mini_valet_plus', 'interior_only_valet', 'full_valet', 'dog_owners_valet']
      },
      {
        name: 'Door shuts, door cards, centre console and dash wiped',
        in: ['mini_valet', 'mini_valet_plus', 'full_valet', 'dog_owners_valet']
      },
      {
        name: 'Interior windows cleaned',
        in: ['mini_valet_plus', 'interior_only_valet', 'full_valet', 'dog_owners_valet']
      },
      {
        name: 'Upholstery shampooed, or leather cleaned and conditioned',
        in: ['interior_only_valet', 'full_valet', 'dog_owners_valet']
      },
      { name: 'Carpet and mats shampooed', in: ['interior_only_valet', 'full_valet', 'dog_owners_valet'] },
      { name: 'Spare wheel area vacuumed', in: ['interior_only_valet', 'full_valet'] },
      { name: 'Pet hair removal', in: ['dog_owners_valet'] }
    ]
  }
]

// Does this wash score this row. One place, so the table and the cards cannot
// read the matrix two different ways.
export const scores = (row, slug) => row.in.includes(slug)

// How many rows there are to score, printed as the denominator on the phone
// cards. Derived, so it cannot drift from the matrix: fourteen today.
export const FEATURE_COUNT = FEATURE_GROUPS.reduce((total, group) => total + group.rows.length, 0)

// Everything one wash includes, in the page's row order, grouped. Used by the
// phone cards, which have no neighbouring column to compare against and so
// have to carry the whole list themselves.
export const includedBy = slug =>
  FEATURE_GROUPS.map(group => ({
    id: group.id,
    title: group.title,
    none: group.none,
    items: group.rows.filter(row => scores(row, slug)).map(row => row.name)
  }))

// How many of the fourteen rows a wash scores. 3, 4, 8, 5, 12 and 10.
//
// It is derived and never typed, so it cannot drift from the ticks, and it is
// the number the phone cards print beside "What's included" while the list is
// still folded away. Six collapsed cards showing 3, 4, 8, 5, 12 and 10 is a
// comparison a phone can hold on one screen; six open lists of fifty two
// items between them is four thousand pixels of scrolling and no comparison
// at all.
export const includedCount = slug =>
  FEATURE_GROUPS.reduce((total, group) => total + group.rows.filter(row => scores(row, slug)).length, 0)
